import React, { useRef, useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import { Link } from "react-router-dom";
import { PenSquare, Trash2Icon, Search } from "lucide-react";
import Spinner from "components/Spinner";
import { Modal } from "react-bootstrap";
import axiosClient from "../axios-client";
import { setTableData, setCurrentTableDataKey, clearTableData } from "../redux/tableSlice";
// import TableSkeleton from "./skeleton/TableSkeleton";
// import Pagination from "./Pagination";
import { ITEMS_PER_PAGE } from "../constants/common";
import { hubFetchJson } from "utils/apiUtils";
import Pagination from "./Pagination";
import LoadingComponent from "./LoadingComponent";

const TableComponent = ({
  apiRoute,
  query,
  columnHeaders,
  title = "",
  canCreate = false,
  canEdit = false,
  canDelete = false,
  editButtonLink = "",
  deleteButtonLink = "",
  createButtonLink = "",
  setData,
  clearData,
  showAction = false,
  onEditClick,
  onDeleteClick,
  showEditButton = false,
  showDeleteButton = false,
  editButtonText = "",
  deleteButtonText = "",
  refreshTrigger = 0,
  noBorderRadius = false,
  showSearch = true,
  searchPlaceholder = "Search…",
  rowHoverClass = "hover:bg-s360-stripe",
}) => {
  const initialState = {
    error: null,
  };

  console.log("TableComponent-refreshTrigger", { refreshTrigger });

  const errorRef = useRef("");
  const defaultPageNumber = 1;
  const dispatch = useDispatch();
  const [reload, setReload] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // const [data, setData] = useState(initialState);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [error, setError] = useState(null);

  // Searching
  const [search, setSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("desc");
  // const [currentTableDataKey, setCurrentTableDataKey] = useState("");
  const [isSearching, setSearching] = useState(false);

  // Pagination
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const { tableData, currentTableDataKey } = useSelector((state) => state.table);
  // console.log("TableComponent-tableData", { tableData });

  // sorting
  const [sortBy, setSortBy] = useState("desc");
  const [orderBy, setOrderBy] = useState("created_at");
  const [sortProperty, setSortProperty] = useState("");
  const [defSortCol, setDefSortCol] = useState("");
  const [skip, setSkip] = useState(0);

  const fetchData = async (pageNumber, searchValue = "", limit = 10) => {
    setIsLoading(true);
    setCurrentPage(Number(pageNumber));
    let finalApiRoute;
    // apiRoute may already carry filters (e.g. ?type=bom), so don't hardcode "?"
    const separator = apiRoute.includes("?") ? "&" : "?";

    switch (true) {
      case searchValue !== "":
        finalApiRoute = `${apiRoute}${separator}order_by=${orderBy}&sort_by=${sortBy}&search=${encodeURIComponent(searchValue)}&skip=${(pageNumber - 1) * itemsPerPage}&page=${pageNumber}&limit=${limit}`;
        break;
      case pageNumber > 0:
        finalApiRoute = `${apiRoute}${separator}order_by=${orderBy}&sort_by=${sortBy}&skip=${(pageNumber - 1) * itemsPerPage}&page=${pageNumber}&limit=${limit}`;
        break;
      default:
        break;
    }

    dispatch(setCurrentTableDataKey(finalApiRoute));

    // Always fetch data for pagination, don't rely on cache for page changes
    if (!Object.prototype.hasOwnProperty.call(tableData, finalApiRoute)) {
      try {
        // hubFetchJson, not a bare fetch: it sends the live Hub JWT and
        // replays once if it lapsed. The redux token this used to read is
        // seeded at boot, so the list stopped refreshing five minutes in.
        const response = await hubFetchJson(finalApiRoute, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });
        const { current_page, total_pages, total_count, results, ...rest } = response?.data ?? {};
        const pageNum = Number(current_page) || 1;
        const total = Number(total_count) || 0;
        updateStateValues(pageNum, total);
        dispatch(setCurrentTableDataKey(finalApiRoute));
        setData && dispatch(setData(results));

        dispatch(
          setTableData({
            data: {
              tableDataKey: finalApiRoute,
              results: {
                results,
                currentPage: current_page,
                totalItems: total_count,
                ...rest,
              },
            },
          }),
        );
      } catch (error) {
        updateStateValues(1, 0);
      } finally {
        setIsLoading(false);
      }
    } else {
      // console.log("Data already exists in cache", { finalApiRoute, itemsPerPage });
      const cached = tableData[finalApiRoute];
      const results = cached?.results;
      // Stored shape uses camelCase (currentPage, totalItems); API uses snake_case
      const pageNum = cached?.currentPage ?? cached?.current_page;
      const total = cached?.totalItems ?? cached?.total_count;
      setData && dispatch(setData(results));
      updateStateValues(Number(pageNum) || 1, Number(total) || 0);
    }
  };

  const updateStateValues = (currentPage, totalItems) => {
    setIsLoading(false);
    setCurrentPage(currentPage);
    setTotalItems(totalItems);
    // setTotalPages(totalPages);
  };

  // Debounced search
  const debouncedSearch = useMemo(
    () =>
      debounce(async (searchValue) => {
        // console.log("debouncedSearch", { searchValue });
        setSearchTerm(searchValue);
        await fetchData(1, searchValue, itemsPerPage); // Always reset to first page when searching
      }, 500),
    [currentPage, itemsPerPage], // Empty dependency array to prevent recreation
  );

  const handleDeleteOpen = (item) => {
    setSelectedItem(item);
    setShowDeleteModal(true);
  };

  const handleDeleteClose = () => {
    setShowDeleteModal(false);
    setSelectedItem(null);
  };

  const handleSearchChange = (e) => {
    const { value } = e.target;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const handleDeleteConfirm = async () => {
    console.log("handleDeleteConfirm", { selectedItem });
    // if (!onDeleteClick || !selectedItem) return;

    // try {
    //   setIsLoading(true);
    //   await onDeleteClick(selectedItem);

    //   // Show success message (you can replace this with your preferred notification system)
    //   console.log("Item deleted successfully");

    //   // Refresh data
    //   fetchData(currentPage);
    // } catch (error) {
    //   console.error("Delete error:", error);
    //   setError(error?.response?.data?.message || "Failed to delete item");
    // } finally {
    //   setIsLoading(false);
    //   setShowDeleteModal(false);
    //   setSelectedItem(null);
    // }
  };

  /*
   * Component initiated
   */
  useEffect(() => {
    setSearch("");
    setSearchTerm("");
    fetchData(defaultPageNumber, "", itemsPerPage);
  }, []);

  /*
   * Refetch when refreshTrigger is incremented (e.g. after edit success)
   */
  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchData(currentPage, searchTerm, itemsPerPage);
    }
  }, [refreshTrigger]);

  // Handle page changes
  const handlePageChange = (newPage) => {
    console.log("handlePageChange", { newPage, searchTerm, currentPage });
    setCurrentPage(newPage);
    fetchData(newPage, searchTerm, itemsPerPage);
  };

  // Handle items per page changes
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
    fetchData(1, searchTerm, newItemsPerPage);
  };

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  useEffect(() => {
    return () => {
      dispatch(clearTableData());
    };
  }, []);

  const columnFormatter = (element, col, row) => {
    if (Object.prototype.hasOwnProperty.call(element, "formatter")) {
      return element.formatter(col, row);
    }
    if (!col && col !== 0) {
      return "-";
    }
    return col;
  };

  const classAlign = (element, propertyName) => {
    let className = "";

    if (element[propertyName]) {
      className = `text-${element[propertyName]}`;
    } else {
      className = "text-center";
    }

    if (element.checkHidden) {
      if (tableData[currentTableDataKey] && !tableData[currentTableDataKey][element.checkHidden]) {
        className = "d-none";
      } else {
        className = "";
      }
    }
    return className;
  };

  const tableDataWithKey = tableData[currentTableDataKey];
  const tableDataResults = tableDataWithKey?.results;
  const totalPages = Math.max(
    1,
    tableDataWithKey?.totalPages || Math.ceil(totalItems / itemsPerPage) || 1,
  );

  // console.log("TableComponent-tableDataWithKey", {
  //   tableDataWithKey,
  //   tableDataResults,
  //   currentTableDataKey,
  //   totalItems,
  // });

  return (
    <>
      {(showSearch || title) && (
        <div className="flex items-center gap-3 p-4 flex-wrap">
          {showSearch && (
            <div className="flex items-center gap-2 h-9 px-3 bg-white border border-s360-stroke1 border-b-2 rounded-md min-w-[280px] focus-within:border-s360-violet text-s360-fg3 focus-within:text-s360-fg1">
              <Search className="w-3.5 h-3.5" />
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder={searchPlaceholder}
                className="flex-1 bg-transparent outline-none text-sm text-s360-fg1 placeholder:text-s360-fg-disabled"
              />
            </div>
          )}
          <span className="ml-auto text-xs text-s360-fg3">
            Showing <strong className="text-s360-fg1">{tableDataResults?.length || 0}</strong> of{" "}
            {totalItems}
          </span>
        </div>
      )}
      <div className={`overflow-x-auto ${noBorderRadius ? "" : "rounded-md"} bg-white`}>
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="text-center py-3 px-3.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-s360-fg3 bg-s360-bg2 border-b border-s360-stroke1 w-16">
                #
              </th>
              {columnHeaders.map((item, index) => {
                const alignClass =
                  item.headerAlign === "right"
                    ? "text-right"
                    : item.headerAlign === "center"
                      ? "text-center"
                      : "text-left";
                const hiddenClass =
                  item.checkHidden && tableDataWithKey && !tableDataWithKey[item.checkHidden]
                    ? "hidden"
                    : "";
                return (
                  <th
                    onClick={() => {}}
                    key={index}
                    className={`py-3 px-3.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-s360-fg3 bg-s360-bg2 border-b border-s360-stroke1 whitespace-nowrap ${alignClass} ${hiddenClass}`}
                  >
                    {item?.name}
                  </th>
                );
              })}
              {showAction && (
                <th className="text-right py-3 px-3.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-s360-fg3 bg-s360-bg2 border-b border-s360-stroke1 pr-4">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white">
            {isLoading && (
              <tr>
                <td colSpan={columnHeaders.length + 2} className="py-16 text-center text-s360-fg3">
                  <Spinner bare label="Loading..." size={32} />
                </td>
              </tr>
            )}
            {!isLoading &&
              tableData &&
              tableDataResults?.length > 0 &&
              tableDataResults?.map((item, index) => (
                <tr
                  key={index}
                  className={`transition-colors ${rowHoverClass} border-b border-s360-bg2 last:border-b-0`}
                >
                  <td className="whitespace-nowrap py-3 px-3.5 text-center font-mono text-xs text-s360-fg3 w-16">
                    {index + 1}
                  </td>
                  {columnHeaders.map((element, idx) => (
                    <td
                      key={idx}
                      className="whitespace-nowrap py-3 px-3.5 text-sm text-s360-fg1"
                      style={element.style}
                    >
                      {element.parentDataField
                        ? columnFormatter(
                            element,
                            item[element.parentDataField][element.dataField],
                            item,
                          )
                        : columnFormatter(element, item[element.dataField], item)}
                    </td>
                  ))}

                  {showAction && (
                    <td className="whitespace-nowrap py-3 px-3.5 text-sm text-s360-fg1 pr-4">
                      <div className="flex items-center justify-end gap-1.5">
                        {showEditButton && onEditClick ? (
                          <button
                            className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-s360-stroke1 text-s360-violet bg-white hover:bg-s360-violet-50 transition-colors"
                            onClick={() => onEditClick(item)}
                            title="Edit"
                          >
                            <PenSquare className="h-3.5 w-3.5" />
                          </button>
                        ) : (
                          <Link
                            to={`${editButtonLink}/${item.id}`}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-s360-stroke1 text-s360-violet bg-white hover:bg-s360-violet-50 transition-colors"
                            title="Edit"
                          >
                            <PenSquare className="h-3.5 w-3.5" />
                          </Link>
                        )}

                        {showDeleteButton && (
                          <button
                            className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-s360-stroke1 text-s360-danger bg-white hover:bg-s360-danger-bg transition-colors"
                            onClick={() => handleDeleteOpen(item)}
                            title="Delete"
                          >
                            <Trash2Icon className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            {!isLoading && tableData && tableDataResults?.length === 0 && (
              <tr>
                <td colSpan="100%" className="py-16 text-center text-sm text-s360-fg3 font-medium">
                  No results to display
                </td>
              </tr>
            )}

            {errorRef.current !== "" && (
              <tr>
                <td colSpan="100%" className="py-16 text-center text-sm text-s360-danger font-medium">
                  Error fetching data
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          itemsPerPage={itemsPerPage}
          totalItems={totalItems}
          setItemsPerPage={handleItemsPerPageChange}
        />
      </div>

      {/* <Modal show={showDeleteModal} onHide={handleDeleteClose} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-xl font-semibold text-indigo-900">
            Are you sure you want to delete?
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Modal.Footer className="mt-2" style={{ borderTop: "none" }}>
            <button
              className="modal-footer-btn bg-gray-50 text-red-600 border-red-600 shadow-sm hover:bg-red-500 hover:text-white"
              onClick={handleDeleteClose}
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteConfirm}
              disabled={isLoading}
              className="modal-footer-btn bg-indigo-600 text-white border-indigo-600 shadow-sm hover:bg-indigo-500 border-2"
            >
              {isLoading ? "Deleting..." : "Delete"}
            </button>
          </Modal.Footer>
        </Modal.Body>
      </Modal> */}
    </>
  );
};

export default TableComponent;
